import type { IHelpdeskUploadRequest, IHelpdeskUploadRequestResponse } from "@kuika/kuika-cl-model";

export interface IHelpdeskUploadProgress {
  /** 1-based index of the file being uploaded. */
  current: number;
  total: number;
  percent: number;
}

export interface IHelpdeskUploadDeps {
  requestUpload: (
    ticketNumber: number,
    request: IHelpdeskUploadRequest,
    idempotencyKey: string
  ) => Promise<IHelpdeskUploadRequestResponse>;
  putFile: (uploadUrl: string, file: File, onProgress: (percent: number) => void) => Promise<void>;
  completeUpload: (ticketNumber: number, attachmentId: string) => Promise<void>;
  newKey: () => string;
}

/**
 * Why a file did not upload:
 * - `network`: no response at all. The storage URL is on the ITSM's own host, so this is usually
 *   the browser blocking a cross-origin PUT (the ITSM's CORS list lacks this designer's origin) or
 *   the connection dropping.
 * - `tooLarge`: refused for its size (413).
 * - `expired`: the one-time upload link was used up or ran out (404 / 409).
 * - `other`: anything else.
 */
export type HelpdeskUploadFailureReason = "network" | "tooLarge" | "expired" | "other";

export interface IHelpdeskFailedUpload {
  fileName: string;
  reason: HelpdeskUploadFailureReason;
}

interface IHttpLikeError {
  response?: { status?: number } | null;
}

export function uploadFailureReason(error: unknown): HelpdeskUploadFailureReason {
  const response: IHttpLikeError["response"] = (error as IHttpLikeError | null)?.response;
  if (!response) return "network";
  if (response.status === 413) return "tooLarge";
  if (response.status === 404 || response.status === 409) return "expired";
  return "other";
}

const FAILURE_TEXT: Record<HelpdeskUploadFailureReason, string> = {
  network: "couldn't reach file storage; the network or the browser blocked the upload",
  tooLarge: "the file is too large",
  expired: "the upload link expired; please attach it again",
  other: "the upload was refused"
};

/** "a.png (the file is too large), b.zip (…)": one line for the warning after a send. */
export function describeFailedUploads(failed: readonly IHelpdeskFailedUpload[]): string {
  return failed
    .map((item: IHelpdeskFailedUpload): string => `${item.fileName} (${FAILURE_TEXT[item.reason]})`)
    .join(", ");
}

/**
 * The three-step upload of contract §4.1, one file at a time: ask CoreApi for a create-only storage
 * URL, PUT the bytes there, then have the ITSM verify it. A failed file doesn't stop the others;
 * the failed ones are returned with the reason.
 */
export async function uploadHelpdeskFiles(
  ticketNumber: number,
  ticketMessageId: number,
  files: File[],
  deps: IHelpdeskUploadDeps,
  onProgress: (progress: IHelpdeskUploadProgress) => void
): Promise<IHelpdeskFailedUpload[]> {
  const failed: IHelpdeskFailedUpload[] = [];
  for (let index = 0; index < files.length; index++) {
    const file: File = files[index];
    const current: number = index + 1;
    onProgress({ current, total: files.length, percent: 0 });
    try {
      const upload: IHelpdeskUploadRequestResponse = await deps.requestUpload(
        ticketNumber,
        {
          fileName: file.name,
          contentType: file.type || "application/octet-stream",
          fileSizeBytes: file.size,
          ticketMessageId
        },
        deps.newKey()
      );
      await deps.putFile(upload.uploadUrl, file, (percent: number): void =>
        onProgress({ current, total: files.length, percent })
      );
      await deps.completeUpload(ticketNumber, upload.attachmentId);
    } catch (error) {
      failed.push({ fileName: file.name, reason: uploadFailureReason(error) });
    }
  }
  return failed;
}

/** An idempotency key for one logical write; a retry of the same write reuses it. */
export function newHelpdeskKey(): string {
  const cryptoApi: Crypto | undefined = typeof crypto === "undefined" ? undefined : crypto;
  if (cryptoApi && typeof cryptoApi.randomUUID === "function") return cryptoApi.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}
